// ── the control frame, as the chat hook reads it ────────────────────────────
//
// The ask route ends its stream with a NUL and one JSON frame (nav, links, ui, the
// replies offered, the mood …). useNettiChat peels it off and acts. The parts that
// are pure live here so they can be proved without a browser: reading the frame
// without ever throwing, dressing its links and replies through the contract's
// cleaners (nothing unsafe is drawn or navigated by), and choosing the one place
// the "plainly asked to be taken" safety net may carry the person to.

import type { ChatMsg } from '../../components/netti/NettiChat'
import { cleanLinks, cleanReplies, type ChatLink } from './links'
import type { Mood } from './mood'

/** what the frame can carry, as it arrives: links and replies are json, so they are `unknown` until cleaned */
export interface ChatFrame {
  nav?: string
  href?: string
  acts?: Array<{ kind?: string; name?: string; to?: string }>
  links?: unknown
  replies?: unknown
  chat?: { action?: 'clear' | 'open' | 'forget'; id?: string }
  prefs?: { sounds?: boolean; expressions?: boolean }
  ui?: Record<string, unknown>
  mood?: Mood
  conversation?: { id: string; title: string }
}

/** the frame, or an empty one: a missing or broken frame is no frame, never an error */
export function readControl(control: string): ChatFrame {
  const text = control.trim()
  if (!text) return {}
  try {
    const parsed = JSON.parse(text) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as ChatFrame) : {}
  } catch {
    return {}
  }
}

/** the places and the replies of a frame, safe to draw */
export function dressFrame(frame: ChatFrame): { links: ChatLink[]; replies: string[] } {
  return { links: cleanLinks(frame.links), replies: cleanReplies(frame.replies) }
}

/** the person plainly asked to be TAKEN somewhere */
const WANTS_TO_GO = /\b(take me|bring me|navigate|jump to|go to)\b|\bopen (the |my |up )?/i

/**
 * SAFETY NET. The person asked to be taken somewhere but the model only located
 * instead of navigating: the top place is where they go (the rows still let them
 * pick another). A page on the open web is never opened on its own, only by a tap.
 */
export function safetyNetTarget(question: string, links: ChatLink[]): string | null {
  if (!links.length || !WANTS_TO_GO.test(question)) return null
  return links.find((l) => l.kind !== 'web')?.path ?? null
}

/**
 * A message as it was stored or fetched, made safe to draw: its links and replies
 * go through the contract's cleaners (an old {label, path} link gains its kind),
 * and `via` is kept only as the one value it can be.
 */
export function dressMessage(m: ChatMsg): ChatMsg {
  const links = cleanLinks(m.links)
  const replies = cleanReplies(m.replies)
  const { links: _l, replies: _r, via: _v, ...rest } = m
  return {
    ...rest,
    ...(links.length ? { links } : {}),
    ...(replies.length ? { replies } : {}),
    ...(m.via === 'chip' ? { via: 'chip' as const } : {}),
  }
}
